import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import api from '@/services/api'
import { StripePayment } from '@/components/payment/StripePayment'

export default function PayLink() {
  const { token } = useParams<{ token: string }>()
  const [link, setLink] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [clientSecret, setClientSecret] = useState('')
  const [starting, setStarting] = useState(false)
  const [paid, setPaid] = useState(false)

  useEffect(() => {
    api.get(`/payment-links/${token}`)
      .then((r) => setLink(r.data))
      .catch((e) => setError(e.response?.data?.detail || 'This payment link is not valid.'))
      .finally(() => setLoading(false))
  }, [token])

  const startPay = async () => {
    setStarting(true); setError('')
    try {
      const r = await api.post(`/payment-links/${token}/pay`)
      setClientSecret(r.data.client_secret)
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Could not start the payment. Please try again.')
    } finally {
      setStarting(false)
    }
  }

  const onSuccess = async (paymentIntent: any) => {
    try {
      await api.post(`/payment-links/${token}/confirm`, { payment_intent_id: paymentIntent?.id })
      setPaid(true)
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Payment went through but confirmation failed — please contact us.')
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-xl shadow p-6">
        <div className="text-center mb-4">
          <img
            src="/images/silux_mobile_logo.png"
            alt="Silux Mobile"
            className="h-12 w-auto mx-auto mb-3"
          />
          <p className="text-sm text-gray-500">Secure Payment</p>
        </div>

        {loading && <p className="text-center text-gray-500 py-8">Loading…</p>}

        {!loading && error && !clientSecret && !paid && (
          <div className="bg-red-50 text-red-700 rounded p-3 text-sm text-center">{error}</div>
        )}

        {paid && (
          <div className="text-center py-6">
            <div className="text-4xl mb-2">✅</div>
            <p className="font-semibold text-gray-900">Payment successful</p>
            <p className="text-sm text-gray-500 mt-1">Thank you. A receipt has been recorded on your account.</p>
          </div>
        )}

        {!loading && link && !paid && (
          <>
            <div className="border rounded-lg p-4 mb-4 bg-gray-50">
              <div className="flex justify-between text-sm py-1">
                <span className="text-gray-500">For</span>
                <span className="font-medium text-right">{link.reason}</span>
              </div>
              {link.order_number && (
                <div className="flex justify-between text-sm py-1">
                  <span className="text-gray-500">Order</span>
                  <span className="font-medium">{link.order_number}</span>
                </div>
              )}
              <div className="flex justify-between text-base py-1 border-t mt-1 pt-2">
                <span className="text-gray-500">Amount</span>
                <span className="font-bold text-[#38A146]">£{Number(link.amount).toFixed(2)}</span>
              </div>
            </div>

            {error && <div className="bg-red-50 text-red-700 rounded p-3 text-sm mb-3">{error}</div>}

            {!clientSecret ? (
              <button
                onClick={startPay}
                disabled={starting}
                className="w-full bg-[#38A146] text-white font-semibold py-3 rounded-lg hover:opacity-90 disabled:opacity-50"
              >
                {starting ? 'Please wait…' : `Pay £${Number(link.amount).toFixed(2)}`}
              </button>
            ) : (
              <StripePayment
                clientSecret={clientSecret}
                amount={Number(link.amount)}
                onSuccess={onSuccess}
                onError={(m) => setError(m)}
              />
            )}
          </>
        )}
      </div>
    </div>
  )
}
